import * as THREE from 'three';
import type { WeaponKit } from './human-weapons.js';

/** Chamfered working surfaces; dimensions and centre agree with BoxGeometry. */
export function weaponBox(k: WeaponKit, parent: THREE.Object3D, material: THREE.Material, w: number, h: number, d: number, x = 0, y = 0, z = 0) {
  const bevel = Math.min(.004, w * .16, h * .16, d * .16), shape = new THREE.Shape();
  shape.moveTo(-w / 2 + bevel, -h / 2 + bevel); shape.lineTo(w / 2 - bevel, -h / 2 + bevel);
  shape.lineTo(w / 2 - bevel, h / 2 - bevel); shape.lineTo(-w / 2 + bevel, h / 2 - bevel); shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: d - 2 * bevel, bevelEnabled: true, bevelSize: bevel, bevelThickness: bevel, bevelSegments: 1, steps: 1 });
  geometry.translate(0, 0, -d / 2 + bevel);
  tileWeaponPlate(geometry);
  return k.mesh(parent, geometry, material, x, y, z);
}

/** Extruded plates have metre-space UVs; keep the forge grain below a millimetre. */
export function tileWeaponPlate(geometry: THREE.BufferGeometry) {
  const uv = geometry.getAttribute('uv'); for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * 12, uv.getY(i) * 12);
  return geometry;
}

export type BladeRow = [y: number, halfWidth: number, halfDepth: number];
/** Closed blade with ground edges and a recessed fuller, rather than a dark bar laid over its face. */
export function forgedBlade(k: WeaponKit, parent: THREE.Object3D, rows: BladeRow[], fuller = true) {
  const across = [-1, -.72, -.30, -.16, 0, .16, .30, .72, 1];
  const section = fuller ? [.05, .84, 1, .66, .62, .66, 1, .84, .05] : [.05, .8, .95, .98, 1, .98, .95, .8, .05];
  const vertices: number[] = [], uvs: number[] = [];
  const point = (row: BladeRow, column: number, side: number) => [row[1] * across[column], row[0], side * row[2] * section[column]];
  const triangle = (a: number[], b: number[], c: number[]) => {
    vertices.push(...a, ...b, ...c); for (const p of [a,b,c]) uvs.push(p[0] * 14, -p[1] * 3);
  };
  for (let r = 0; r < rows.length - 1; r++) for (let i = 0; i < across.length - 1; i++) for (const side of [-1, 1]) {
    const a = point(rows[r], i, side), b = point(rows[r + 1], i, side), c = point(rows[r + 1], i + 1, side), d = point(rows[r], i + 1, side);
    if (side === 1) { triangle(a,b,d); triangle(b,c,d); } else { triangle(a,d,b); triangle(b,d,c); }
  }
  // Close both cutting edges and the blade's shoulder and point.
  for (let r = 0; r < rows.length - 1; r++) for (const i of [0, across.length - 1]) {
    const a = point(rows[r], i, 1), b = point(rows[r + 1], i, 1), c = point(rows[r + 1], i, -1), d = point(rows[r], i, -1);
    if (i === 0) { triangle(a,d,b); triangle(b,d,c); } else { triangle(a,b,d); triangle(b,c,d); }
  }
  for (const r of [0, rows.length - 1]) for (let i = 0; i < across.length - 1; i++) {
    const a = point(rows[r], i, 1), b = point(rows[r], i + 1, 1), c = point(rows[r], i + 1, -1), d = point(rows[r], i, -1);
    if (r === 0) { triangle(a,b,d); triangle(b,c,d); } else { triangle(a,d,b); triangle(b,d,c); }
  }
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2)); geometry.computeVertexNormals();
  return k.mesh(parent, geometry, k.material.blade);
}
